import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Navbar, Footer } from "../components/Layout";
import { Volume2, Search, Sparkles } from "lucide-react";
import { useTTS } from "../hooks/useTTS";
import { VoiceSelector } from "../components/VoiceSelector";
import { useSEO } from "../hooks/useSEO";

const LEVEL_META = {
  N5: { color: "#34D399", label: "Débutant", desc: "~200 mots, premiers pas" },
  N4: { color: "#FFD166", label: "Intermédiaire bas", desc: "~257 mots, vie quotidienne" },
  N3: { color: "#E94A3F", label: "Intermédiaire", desc: "~256 mots, articles, séries, travail" },
};

const Vocabulary = () => {
  const [level, setLevel] = useState("N5");
  const [category, setCategory] = useState("");
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const { speak, prefetch } = useTTS();

  useSEO({
    title: "Vocabulaire japonais gratuit : 636 mots JLPT N5 avec audio | Japonais Pas à Pas",
    description: "636 mots de vocabulaire japonais classés par thème (nourriture, voyage, travail), avec hiragana, romaji, audio natif et traduction française. JLPT N5. Gratuit.",
    canonical: "https://japonais-pas-a-pas.com/vocabulaire",
  });

  useEffect(() => {
    api.get("/vocab/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ level, limit: "1000" });
    if (category) params.append("category", category);
    api.get(`/vocab/public?${params}`).then((r) => {
      setItems(r.data.data || []);
      setTotal(r.data.total || 0);
    }).finally(() => setLoading(false));
    setCategory(""); // reset category when level changes
    // eslint-disable-next-line
  }, [level]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ level, limit: "1000" });
    if (category) params.append("category", category);
    api.get(`/vocab/public?${params}`).then((r) => {
      const data = r.data.data || [];
      setItems(data);
      setTotal(r.data.total || 0);
      // Warm up audio cache for the first ~40 words so the first click is instant
      prefetch(data.slice(0, 40).map((w) => w.jp));
    }).finally(() => setLoading(false));
  }, [category, level, prefetch]);

  const filtered = useMemo(() => {
    if (!query.trim()) return items;
    const q = query.toLowerCase();
    return items.filter((w) =>
      (w.jp || "").includes(q) ||
      (w.kana || "").toLowerCase().includes(q) ||
      (w.romaji || "").toLowerCase().includes(q) ||
      (w.fr || "").toLowerCase().includes(q)
    );
  }, [items, query]);

  // Group filtered words by category
  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((w) => {
      const c = w.category || "Autres";
      if (!g[c]) g[c] = [];
      g[c].push(w);
    });
    return g;
  }, [filtered]);

  const meta = LEVEL_META[level];

  return (
    <div className="washi-bg min-h-screen">
      <Navbar />

      <header className="max-w-6xl mx-auto px-6 sm:px-12 pt-16 pb-10" data-testid="vocab-header">
        <div className="inline-flex items-center gap-2 bg-white px-4 py-1.5 rounded-full border border-[#1A2A40]/15 text-xs font-bold tracking-widest text-[#E94A3F] mb-5">
          <Sparkles className="w-3.5 h-3.5" /> 636+ MOTS · GRATUIT · AUDIO NATIF
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-[#1A2A40] leading-[1.05] mb-4">
          Vocabulaire japonais <span className="text-[#E94A3F]">par thème</span>
        </h1>
        <p className="text-base sm:text-lg text-[#4A5568] max-w-3xl mb-6">
          Tous les mots des niveaux JLPT N5, N4 et N3, classés par thème (voyage, business, famille…),
          avec leur lecture (kana + romaji), leur traduction et la <strong>prononciation native japonaise</strong> par nos voix Atsuko 👩🏻 ou Alexis 👨🏻.
        </p>
        <div className="flex flex-wrap items-center gap-3" data-testid="vocab-voice-picker">
          <span className="text-xs font-bold uppercase tracking-widest text-[#A0AEC0]">Voix audio :</span>
          <VoiceSelector variant="block" />
        </div>
      </header>

      {/* Level pills */}
      <div className="max-w-6xl mx-auto px-6 sm:px-12 mb-8">
        <div className="flex flex-wrap gap-3" data-testid="level-pills">
          {Object.entries(LEVEL_META).map(([lvl, m]) => (
            <button
              key={lvl}
              onClick={() => setLevel(lvl)}
              data-testid={`level-pill-${lvl}`}
              className={`px-5 py-2.5 rounded-xl font-bold text-sm transition-all border-2 ${
                level === lvl
                  ? "text-white shadow-brut-sm"
                  : "bg-white text-[#1A2A40] border-[#1A2A40]/10 hover:border-[#1A2A40]/30"
              }`}
              style={level === lvl ? { background: m.color, borderColor: m.color } : {}}
            >
              JLPT {lvl} · <span className="font-medium opacity-80">{m.label}</span>
            </button>
          ))}
        </div>
        <p className="text-xs text-[#A0AEC0] mt-3 italic">{meta.desc} · <strong>{total} mots</strong> en {level}</p>
      </div>

      {/* Filters */}
      <div className="max-w-6xl mx-auto px-6 sm:px-12 mb-10">
        <div className="bg-white rounded-2xl border border-[#1A2A40]/10 shadow-sm p-4 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A0AEC0]" />
            <input
              type="text"
              placeholder="Rechercher (FR, kana, romaji, kanji)..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="vocab-search"
              className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-[#1A2A40]/15 text-sm focus:outline-none focus:border-[#E94A3F]"
            />
          </div>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            data-testid="vocab-category-select"
            className="px-3 py-2.5 rounded-xl border border-[#1A2A40]/15 text-sm bg-white focus:outline-none focus:border-[#E94A3F]"
          >
            <option value="">Tous les thèmes</option>
            {(categories[level] || []).map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <span className="text-xs text-[#A0AEC0]">{filtered.length} mot{filtered.length > 1 ? "s" : ""} affiché{filtered.length > 1 ? "s" : ""}</span>
        </div>
      </div>

      {/* Words grid */}
      <main className="max-w-6xl mx-auto px-6 sm:px-12 pb-20">
        {loading ? (
          <div className="text-center py-16 text-[#A0AEC0]">Chargement…</div>
        ) : Object.keys(grouped).length === 0 ? (
          <div className="text-center py-16 text-[#A0AEC0]">Aucun mot trouvé.</div>
        ) : (
          Object.entries(grouped).map(([cat, words]) => (
            <section key={cat} className="mb-10" data-testid={`category-section-${cat}`}>
              <h2 className="font-display font-black text-2xl text-[#1A2A40] mb-4 flex items-baseline gap-3">
                {cat} <span className="text-xs font-medium text-[#A0AEC0]">{words.length} mots</span>
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {words.map((w, i) => (
                  <div
                    key={`${w.jp}-${i}`}
                    className="bg-white rounded-xl p-4 border border-[#1A2A40]/10 hover:shadow-brut-sm transition-shadow flex items-start gap-3"
                    data-testid={`vocab-card-${w.jp}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-jp font-bold text-2xl text-[#1A2A40] leading-tight">{w.jp}</div>
                      {w.kana && w.kana !== w.jp && (
                        <div className="font-jp text-sm text-[#4A5568] mt-0.5">{w.kana}</div>
                      )}
                      <div className="font-mono text-xs text-[#E94A3F] mt-1">{w.romaji}</div>
                      <div className="text-sm text-[#4A5568] italic mt-1.5">{w.fr}</div>
                    </div>
                    <button
                      onClick={() => speak(w.jp)}
                      data-testid={`speak-${w.jp}`}
                      className="shrink-0 p-2 rounded-lg bg-[#F9F6F0] hover:bg-[#FFD166] transition-colors border border-[#1A2A40]/10"
                      aria-label={`Écouter ${w.jp}`}
                    >
                      <Volume2 className="w-4 h-4 text-[#1A2A40]" />
                    </button>
                  </div>
                ))}
              </div>
            </section>
          ))
        )}

        {/* Premium CTA */}
        <div className="mt-16 bg-[#1A2A40] text-white rounded-3xl p-8 sm:p-12 shadow-brut" data-testid="vocab-cta">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#FFD166] text-[#1A2A40] px-3 py-1 rounded-full text-xs font-black tracking-widest mb-4">
                ALLER PLUS LOIN
              </div>
              <h3 className="font-display font-black text-3xl sm:text-4xl mb-3 leading-tight">
                Apprendre ces mots, c'est mieux <span className="text-[#FFD166]">en contexte</span>.
              </h3>
              <p className="text-white/80 leading-relaxed">
                Les 636 mots de cette page sont gratuits. Mais pour vraiment les retenir, il faut les voir
                en grammaire, en dialogues, et les réviser au bon moment. Nos 200 leçons + le système SRS
                font ce travail pour vous.
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <Link
                to="/register"
                data-testid="vocab-cta-register"
                className="bg-[#E94A3F] hover:bg-[#d83a2f] text-white font-bold py-4 px-6 rounded-xl text-center transition-colors flex items-center justify-center gap-2"
              >
                <Sparkles className="w-5 h-5" /> Essai gratuit 3 jours
              </Link>
              <Link
                to="/lessons"
                className="border-2 border-white/30 hover:border-white/60 text-white font-bold py-4 px-6 rounded-xl text-center transition-colors"
              >
                Voir les leçons →
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Vocabulary;
